import { ChangeEvent } from 'react';
import { snapshot } from 'valtio';
import {
  AppPages,
  navigate,
  navigateWithFilters,
} from '../../../common/routing/app-navigator';
import { Nullable } from 'common-types';
import { loadFilters } from '../../active-issues/actions/active-issues-actions';
import { loadIssues } from '../../active-issues/api/active-issues-api';
import IssuesStore, {
  IssuesStoreState,
} from '../../active-issues/stores/issues-store';
import ResolvedIssuesStore, {
  ResolvedIssuesStoreState,
} from '../../resolved-issues/stores/resolved-issues-store';
import {
  FilterIssueType,
  IssueDetails,
  JiraTicketRef,
  SlackNotification,
} from '../types/issues-types';
import { RawIssue } from '../types/raw-issue-type';
import { convertIssue } from '../utils/convert-issue-utils';
import { createIssueDetails } from '../utils/issue-utils';
import { FilterElements } from '../../../common/filters-utils/filters-utils';

const createIssueStoreActions = (
  store: typeof IssuesStore | typeof ResolvedIssuesStore,
  initalState: Partial<IssuesStoreState> | Partial<ResolvedIssuesStoreState>,
) => {
  const setStoreIssues = (rawIssues?: RawIssue[], update?: boolean) => {
    if (rawIssues) {
      if (update) {
        store.issues = [...rawIssues.map(convertIssue)];
      } else {
        store.issues = [
          ...store.issues,
          ...rawIssues.map((rawIssue, index) =>
            convertIssue(rawIssue, index + store.issues.length),
          ),
        ];
      }
    } else {
      store.issues = [];
    }
  };
  const setPolicyFixModal = (value: boolean) => {
    (store as IssuesStoreState).isPolicyFixOpen = value;
  };
  const setStoreTotalIssues = (totalIssues: number) => {
    store.totalIssues = totalIssues;
  };

  const setStoreTotalActiveIssues = (totalActiveIssues: number) => {
    store.totalActiveIssues = totalActiveIssues;
  };

  const setStoreTotalResolvedIssues = (totalResolvedIssues: number) => {
    store.totalResolvedIssues = totalResolvedIssues;
  };

  const setStoreTotalIssuesFiltered = (totalFilteredIssues: number) => {
    store.totalFilteredIssues = totalFilteredIssues;
  };

  const setStoreIssuesOffset = (offset: number) => {
    store.offset = offset;
  };
  const setStoreIssuesFiltersType = (issuesFilters: FilterIssueType) => {
    store.issuesFiltersType = issuesFilters;
  };

  const setStoreIssuesFiltersTypeSearch = (
    issuesFilters: FilterIssueType,
  ): void => {
    const { searchValues } = snapshot(store);
    store.issuesFiltersTypeSearch = Object.keys(issuesFilters).reduce(
      (acc, key) => {
        acc[key] = searchValues[key]
          ? issuesFilters[key].filter((f: FilterElements) =>
              f.label
                .toLocaleLowerCase()
                .includes(searchValues[key].toLowerCase()),
            )
          : issuesFilters[key];
        return acc;
      },
      {} as FilterIssueType,
    );
  };

  const setSearchValues = (type: string, searchValue: string) => {
    const index = store.searchValues.findIndex(key => key.fieldName === type);
    if (index !== -1) {
      store.searchValues[index].value = searchValue;
    } else {
      store.searchValues.push({ fieldName: type, value: searchValue });
    }
  };

  const setModalVisibility = (payload: boolean): void => {
    store.isModalVisible = payload;
  };

  const closeIssueModal = (): void => {
    setModalVisibility(false);
    setSelectedIssue(null);
    setSelectedIssueId(null);
  };

  const closeIssueModalWithoutNavigate = (): void => {
    setModalVisibility(false);
    setSelectedIssue(null);
  };

  const openIssueModal = (): void => {
    setModalVisibility(true);
  };

  const setSelectedIssue = (issue: IssueDetails | null) => {
    store.selectedIssue = issue ? createIssueDetails(issue) : null;
    setModalVisibility(true);
  };

  const toggleFiltersPanel = () => {
    store.filtersOpen = !store.filtersOpen;
  };

  const clearFilters = () => {
    store.filterIssuesBy = {};
    store.offset = 0;
    store.numberOfFilters = 0;
    store.sliderValuePriority = [0, 100];
    store.searchValue = '';
    navigate(AppPages.Issues);
    loadIssues({ update: true, cache: false });
    loadFilters();
  };

  const clearStore = () => {
    initalState.filterIssuesBy = {};
    Object.keys(initalState).forEach(key => {
      store[key] = initalState[key];
    });
  };

  const setPolicyDisableModal = (payload: boolean) => {
    store.policyDisableModal = payload;
  };

  const setApplicationIrrelevantModal = (payload: boolean) => {
    store.applicationIrrelevantModal = payload;
  };

  const setShowCreateJiraTicketDialog = (payload: boolean) => {
    store.showCreateJiraTicketDialog = payload;
  };

  const setShowSendSlackMessageDialog = (payload: boolean) => {
    store.showSendSlackMessageDialog = payload;
  };

  const openQuickFixModal = () => {
    store.isQuickFixModalVisible = true;
  };

  const setQuickFixModalVisibility = (isVisible: boolean) => {
    store.isQuickFixModalVisible = isVisible;
  };

  const openQuickFixPermissionDeniedModal = () => {
    store.isQuickFixPermissionDeniedModalVisible = true;
  };

  const setQuickFixPermissionDeniedModalVisibility = (isVisible: boolean) => {
    store.isQuickFixPermissionDeniedModalVisible = isVisible;
  };

  const setFilesDiffModalVisibility = (isVisible: boolean) => {
    store.isFilesDiffModalVisible = isVisible;
  };

  const setSliderValuePriority = (range: number[]) => {
    store.sliderValuePriority = range;
  };

  const setLastDrawerHeight = (height: number) => {
    store.lastDrawerHeight = height;
  };

  const setIsActiveJiraConnection = (isActiveJiraConnection: boolean) => {
    store.isActiveJiraConnection = isActiveJiraConnection;
  };

  const setIsActiveSlackConnection = (isActiveSlackConnection: boolean) => {
    store.isActiveSlackConnection = isActiveSlackConnection;
  };

  const setSelectedIssueId = (issueID: Nullable<string>) => {
    store.selectedIssueId = issueID;
  };

  const setExclusionIssueComment = (e: ChangeEvent<HTMLTextAreaElement>) => {
    if (store.selectedIssue) {
      store.selectedIssue.exclusionIssueComment = e.target.value;
    }
  };

  const setExclusionPolicyComment = (e: ChangeEvent<HTMLTextAreaElement>) => {
    if (store.selectedIssue) {
      store.selectedIssue.exclusionPolicyComment = e.target.value;
    }
  };

  const setExclusionAppComment = (e: ChangeEvent<HTMLTextAreaElement>) => {
    if (store.selectedIssue) {
      store.selectedIssue.exclusionAppComment = e.target.value;
    }
  };

  const addJiraTicketToRawIssue = (jiraTicketRef: JiraTicketRef) => {
    const { selectedIssue } = snapshot(store);
    if (selectedIssue) {
      const rowIssue = store.issues.find(
        issue => issue.issueId === selectedIssue.issueId,
      );
      rowIssue && rowIssue.jiraTickets.push(jiraTicketRef);
    }
  };

  const setJiraTicketsToRawIssue = (
    issueId: string,
    jiraTicketsRef: JiraTicketRef[],
  ) => {
    const rowIssue = store.issues.find(issue => issue.issueId === issueId);
    if (rowIssue) {
      rowIssue.jiraTickets = jiraTicketsRef;
    }
  };

  const pushSlackNotificationToRawIssue = (
    issueId: string,
    slackNotification: SlackNotification,
  ) => {
    const rowIssue = store.issues.find(issue => issue.issueId === issueId);
    if (rowIssue) {
      rowIssue.slackNotification?.push(slackNotification);
    }
  };

  const addJiraTicketToSelectedIssue = (jiraTicketRef: JiraTicketRef) => {
    const { selectedIssue } = snapshot(store);
    if (selectedIssue && store.selectedIssue) {
      store.selectedIssue.tickets.push(jiraTicketRef);
    }
  };

  const setLoadingJiraStatusUpdate = (payload: boolean) => {
    store.loadingJiraTicketsStatus = payload;
  };

  const setDrawerJiraTicketsStatus = (jiraTicketStatus: JiraTicketRef[]) => {
    const { selectedIssue } = snapshot(store);
    if (selectedIssue && store.selectedIssue) {
      store.selectedIssue.tickets = jiraTicketStatus;
    }
  };

  const setLoadingIssue = (value: boolean) => {
    store.loadingIssue = value;
  };

  const setStoreIssuesStatistics = (applicationStatistics: FilterIssueType) => {
    store.applicationStatistics = applicationStatistics;
  };

  const setCommentModal = (open: boolean) => {
    store.commentModal = open;
  };

  const setIssueComment = () => {
    if (store.selectedIssue) {
      store.selectedIssue.comment = store.issueComment;
    }
  };

  const setIssueCommentTemp = (e: ChangeEvent<HTMLTextAreaElement>) => {
    store.issueComment = e.target.value;
  };

  const clearFilter = (type: string) => {
    store.filterIssuesBy[type] = [];
    navigateWithFilters(AppPages.Issues, snapshot(store.filterIssuesBy));
    loadIssues({ update: true });
    loadFilters();
  };

  const setSnoozeIssueExpiredDate = (date?: Date) => {
    if (store.selectedIssue) {
      store.selectedIssue.snoozeExpiredDate = date;
    }
  };

  const setSearchValue = (value: string) => {
    store.searchValue = value;
  };

  const onSearchValueChange = e => {
    const { issues } = snapshot(store);
    setSearchValue(e.target.value);
    if (!e.target.value) loadIssues({ update: true, cache: false });
    const res = issues.filter(issue => {
      const { mainTitle, issueId, app, owner, category } = issue;
      return [mainTitle, issueId, app.name, owner[0], category]
        .toString()
        .toLocaleLowerCase()
        .trim()
        .includes(e.target.value);
    });
    store.issues = res.length ? res : issues;
  };

  return {
    setDrawerJiraTicketsStatus,
    setStoreIssues,
    setStoreTotalIssues,
    setStoreTotalResolvedIssues,
    setStoreTotalIssuesFiltered,
    setStoreIssuesOffset,
    setStoreIssuesFiltersType,
    setStoreIssuesFiltersTypeSearch,
    setModalVisibility,
    closeIssueModal,
    closeIssueModalWithoutNavigate,
    openIssueModal,
    setSelectedIssue,
    toggleFiltersPanel,
    clearFilters,
    clearStore,
    setPolicyDisableModal,
    setApplicationIrrelevantModal,
    setShowCreateJiraTicketDialog,
    openQuickFixModal,
    setQuickFixModalVisibility,
    openQuickFixPermissionDeniedModal,
    setQuickFixPermissionDeniedModalVisibility,
    setFilesDiffModalVisibility,
    setSliderValuePriority,
    setLastDrawerHeight,
    setIsActiveJiraConnection,
    setSelectedIssueId,
    setExclusionIssueComment,
    setExclusionPolicyComment,
    setExclusionAppComment,
    addJiraTicketToRawIssue,
    addJiraTicketToSelectedIssue,
    setLoadingJiraStatusUpdate,
    setLoadingIssue,
    setStoreIssuesStatistics,
    setStoreTotalActiveIssues,
    setCommentModal,
    setIssueComment,
    setIssueCommentTemp,
    setPolicyFixModal,
    setSearchValues,
    clearFilter,
    setSnoozeIssueExpiredDate,
    setJiraTicketsToRawIssue,
    setShowSendSlackMessageDialog,
    setIsActiveSlackConnection,
    pushSlackNotificationToRawIssue,
    setSearchValue,
    onSearchValueChange,
  };
};

export default createIssueStoreActions;
